/**
 * AR Technical Demo (F0 / GEN-2256) — eager registration of the technical-demo
 * tools (checkpoints + navigation) into the demo-script registry.
 *
 * THROWAWAY DEMO CODE. The technical-demo scripted beats fire from whatever page
 * the presenter is on, and must stand up the world they need cold (non-linear
 * entry, storyboard §2b). Page UIs are built by F1-F4, but their nav targets and
 * the scene-checkpoint state must already be reachable so every beat resolves
 * before those pages mount. This hook registers:
 *
 *  - Checkpoint tools (`ar_tech_checkpoint_*`): apply a scene's prerequisite
 *    fixture state via `demo-checkpoints.ts`. Registry-only, no navigation.
 *  - Navigation tools (`ar_tech_navigate_*`): jump to the real AR surfaces the
 *    technical flows live on (remittance detail, collection threads,
 *    reconciliation view). These target EXISTING routes; F1-F4 wire the demo
 *    rendering on those pages.
 *
 * Like `useArDemoGlobalNavTools`, this registers ONLY into the demo-script
 * registry (never the live agent module) so it never clobbers a page's
 * `currentPage`/tool set. Called once from `ArDemoControls`.
 */

import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import type { FrontendToolDefinition } from '@/hooks/useFrontendTool';
import { registerArDemoTools } from './demo-script';
import { getArDemoState } from './fixture-store';
import { TECHNICAL_CHECKPOINTS } from './demo-checkpoints';
import {
  RECONCILIATION_DEMO_PATH,
  CASH_RECEIPT_INGESTION_DEMO_PATH,
} from './demo-routes';

const NO_ARGS_SCHEMA = z.toJSONSchema(z.object({})) as Record<string, unknown>;

function tool(
  name: string,
  description: string,
  execute: () => unknown,
  awaitable = false,
): FrontendToolDefinition {
  return {
    name,
    description,
    awaitable,
    schema: { name, description, parameters: NO_ARGS_SCHEMA, awaitable },
    execute,
  };
}

/**
 * Register the technical-demo checkpoint + navigation tools. The checkpoints are
 * pure store mutations (no hooks), so they could be registered at module load,
 * but the navigation tools need a router `navigate`, so the whole set is
 * registered from this hook (called from the always-mounted ArDemoControls).
 */
export function useTechnicalDemoTools(): void {
  const navigate = useNavigate();

  const tools = useMemo<FrontendToolDefinition[]>(() => {
    const checkpointTools: FrontendToolDefinition[] = Object.entries(
      TECHNICAL_CHECKPOINTS,
    ).map(([fnName, fn]) =>
      tool(
        `ar_tech_checkpoint_${fnName}`,
        `Apply the technical-demo scene prerequisite "${fnName}" to the fixture store.`,
        () => fn(),
      ),
    );

    const navTools: FrontendToolDefinition[] = [
      tool(
        'ar_tech_navigate_remittance',
        'Open the Crestline remittance RA-7741 detail page.',
        () =>
          navigate({
            to: '/accounts-receivable/remittances/$remittanceId',
            params: { remittanceId: getArDemoState().technical.remittanceId },
          }),
      ),
      tool(
        'ar_tech_navigate_short_pay_thread',
        'Open the Crestline short-pay collection thread COL-1042.',
        () => {
          const { technical } = getArDemoState();
          return navigate({
            to: '/accounts-receivable/collections/$customerId/threads/$threadId',
            params: {
              customerId: technical.customerId,
              threadId: technical.shortPayThreadId,
            },
            search: { from: 'threads' },
          });
        },
      ),
      tool(
        'ar_tech_navigate_deduction_thread',
        'Open the Crestline deduction collection thread COL-1043.',
        () => {
          const { technical } = getArDemoState();
          return navigate({
            to: '/accounts-receivable/collections/$customerId/threads/$threadId',
            params: {
              customerId: technical.customerId,
              threadId: technical.deductionThreadId,
            },
            search: { from: 'threads' },
          });
        },
      ),
      tool(
        'ar_tech_navigate_reconciliation',
        "Open the bank-reconciliation view for today's deposits.",
        () =>
          // F4 (GEN-2260) re-points this to its bespoke reconciliation view.
          navigate({
            to: RECONCILIATION_DEMO_PATH,
          }),
      ),
      tool(
        'ar_navigate_cash_receipt_ingestion',
        'Open the blanket cash-receipt ingestion view that sweeps all linked bank accounts, creates cash receipts, matches remittances, and flags short-pays and exceptions.',
        () =>
          navigate({
            to: CASH_RECEIPT_INGESTION_DEMO_PATH,
          }),
      ),
    ];

    return [...checkpointTools, ...navTools];
  }, [navigate]);

  useMemo(() => {
    registerArDemoTools('ar-tech-demo', tools);
  }, [tools]);
}
